import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Button,
  FormContainer,
  FormSection,
  FloatingField,
  InputText,
} from '../../components/aip';
import {
  getCheckTemplate,
  updateCheckTemplate,
  type CheckTemplate,
} from '../../services/manageNewApi';

interface TemplateItem {
  id?: number;
  item_text: string;
  sort_order: number;
}

/**
 * チェックテンプレート編集ページ
 */
export const CheckTemplateEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const templateId = id ? parseInt(id, 10) : null;

  // データ状態
  const [template, setTemplate] = useState<CheckTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // フォーム状態
  const [templateName, setTemplateName] = useState('');
  const [sortOrder, setSortOrder] = useState('0');
  const [items, setItems] = useState<TemplateItem[]>([]);

  // データ取得
  useEffect(() => {
    const fetchData = async () => {
      if (!templateId) {
        setError('IDが指定されていません');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const templateData = await getCheckTemplate(templateId);
        setTemplate(templateData);

        // フォームに値を設定
        setTemplateName(templateData.template_name || '');
        setSortOrder(String(templateData.sort_order || 0));
        setItems(
          templateData.items.map((item, idx) => ({
            id: item.id,
            item_text: item.item_text,
            sort_order: item.sort_order ?? idx,
          }))
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [templateId]);

  // 項目追加
  const handleAddItem = () => {
    setItems([...items, { item_text: '', sort_order: items.length }]);
  };

  // 項目削除
  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // 項目更新
  const handleItemChange = (index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], item_text: value };
    setItems(newItems);
  };

  // 項目の順序入れ替え
  const handleMoveItem = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === items.length - 1) return;

    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    // sort_orderを更新
    newItems.forEach((item, i) => {
      item.sort_order = i;
    });
    setItems(newItems);
  };

  // 保存処理
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!templateId || !templateName.trim()) {
      setError('テンプレート名は必須です');
      return;
    }

    const validItems = items.filter((item) => item.item_text.trim());
    if (validItems.length === 0) {
      setError('少なくとも1つのチェック項目が必要です');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await updateCheckTemplate(templateId, {
        template_name: templateName.trim(),
        sort_order: parseInt(sortOrder, 10) || 0,
        items: validItems.map((item, idx) => ({
          id: item.id,
          item_text: item.item_text.trim(),
          sort_order: idx,
        })),
      });

      navigate('/meeting/manage-new/check-templates');
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        読み込み中...
      </div>
    );
  }

  if (!template && !loading) {
    return (
      <div
        style={{
          padding: '1rem',
          backgroundColor: 'var(--color-red50)',
          color: 'var(--color-alert)',
          borderRadius: '0.5rem',
        }}
      >
        {error || 'チェックテンプレートが見つかりません'}
      </div>
    );
  }

  return (
    <>
      {error && (
        <div
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--color-red50)',
            color: 'var(--color-alert)',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      <FormContainer onSubmit={handleSubmit}>
        <FormSection title="基本情報">
          <FloatingField label="テンプレート名" htmlFor="template_name" required>
            <InputText
              id="template_name"
              name="template_name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="チェックテンプレートの名前"
            />
          </FloatingField>

          <FloatingField label="表示順" htmlFor="sort_order">
            <InputText
              id="sort_order"
              name="sort_order"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              min="0"
            />
          </FloatingField>
        </FormSection>

        <FormSection title="チェック項目" layout="vertical">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
            {items.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem',
                  backgroundColor: 'var(--color-opacity-gray25)',
                  borderRadius: '0.5rem',
                }}
              >
                <span style={{ color: 'var(--text-secondary)', minWidth: '2rem' }}>
                  {index + 1}.
                </span>
                <InputText
                  value={item.item_text}
                  onChange={(e) => handleItemChange(index, e.target.value)}
                  placeholder="チェック項目を入力"
                  style={{ flex: 1 }}
                />
                <Button
                  buttonType="icon-only"
                  size="sm"
                  type="button"
                  onClick={() => handleMoveItem(index, 'up')}
                  disabled={index === 0}
                  aria-label="上へ移動"
                >
                  <span className="material-symbols-outlined">arrow_upward</span>
                </Button>
                <Button
                  buttonType="icon-only"
                  size="sm"
                  type="button"
                  onClick={() => handleMoveItem(index, 'down')}
                  disabled={index === items.length - 1}
                  aria-label="下へ移動"
                >
                  <span className="material-symbols-outlined">arrow_downward</span>
                </Button>
                <Button
                  buttonType="icon-only"
                  size="sm"
                  type="button"
                  state="alert"
                  onClick={() => handleRemoveItem(index)}
                  aria-label="削除"
                >
                  <span className="material-symbols-outlined">delete</span>
                </Button>
              </div>
            ))}

            <Button
              buttonType="outline"
              size="sm"
              type="button"
              onClick={handleAddItem}
              iconPosition="start"
              style={{ alignSelf: 'flex-start' }}
            >
              <span className="material-symbols-outlined">add</span>
              項目を追加
            </Button>
          </div>
        </FormSection>

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <Button
            buttonType="outline"
            size="md"
            type="button"
            onClick={() => navigate('/meeting/manage-new/check-templates')}
            disabled={saving}
          >
            キャンセル
          </Button>
          <Button buttonType="solid-fill" size="md" type="submit" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </FormContainer>
    </>
  );
};

export default CheckTemplateEditPage;

